import { NetworkIntegrityAggregate } from "@app/projects/network-assessment/network-integrity/models";
import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { NetworkIntegrityWidgetInfoFlyout, NetworkSummaryDescription, WidgetMenu } from ".";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityAggregateKey, NetworkIntegrityJobStatus } from "@app/projects/enumerators";
import { NetworkIntegrityDisplayMode } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { WidgetLoading, WidgetHeader, Widget, WidgetBody } from "./Widget";
import { NetworkIntegrityChangeArrow } from "@app/projects/network-assessment/network-integrity/widgets/NetworkIntegrityChangeArrow";

const NetworkSummaryWidget = (): JSX.Element => {
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const project = networkIntegrityContext.project!;
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);

  const [networkSummaryResult, setNetworkSummaryResult] = useState<NetworkIntegrityAggregate[]>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const title = 'Summary';
  const showTotalAfterTermed = networkIntegrityContext.displayMode === NetworkIntegrityDisplayMode.TotalAfterTermed;

  useEffect(() => {
    let cancelled = false;
    setNetworkSummaryResult(null);
    async function fetchSummaryResults() {
      try {
        if (networkIntegrityContext.projectRunConfigurationId) {
          const result = await networkIntegrityService.getNetworkSummaryResult(project.projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes);
          if (!cancelled) setNetworkSummaryResult(result);
        }
      } finally {
        if (!cancelled) setLoading(false);
      };
    };

      if (networkIntegrityContext.isJobStatusLoading === false && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished) {
        setLoading(true);
        fetchSummaryResults();
      }

    return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes]);

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading  additionalClassNames="ni-network-summary"><WidgetHeader title={title} /></WidgetLoading>);

  const getAggregate = (aggregateKey: NetworkIntegrityAggregateKey): NetworkIntegrityAggregate | undefined => {
    return networkSummaryResult && networkSummaryResult.length > 0 ? networkSummaryResult.find(a => a.aggregateKey == aggregateKey) : undefined;
  };

  // True only when Total After Termed is showing and the metric actually shrank.
  const hasDecrease = (aggregate: NetworkIntegrityAggregate | undefined): boolean =>
    showTotalAfterTermed && aggregate != null && aggregate.afterTermedCount != null && aggregate.afterTermedCount < aggregate.count;

  const getMetricValue = (aggregateKey: NetworkIntegrityAggregateKey): string => {
    const aggregate = getAggregate(aggregateKey);
    if (!aggregate) return "---";
    const value = hasDecrease(aggregate) ? aggregate.afterTermedCount : aggregate.count;
    return value.toLocaleString();
  };

  const renderDecreaseIndicator = (aggregateKey: NetworkIntegrityAggregateKey, testId: string): JSX.Element | null => {
    const aggregate = getAggregate(aggregateKey);
    if (!hasDecrease(aggregate)) return null;
    const decrease = aggregate.count - aggregate.afterTermedCount;
    return (
      <span className="ni-network-summary__decrease-indicator" data-testid={testId}>
        <NetworkIntegrityChangeArrow direction="decrease" />
        {"("}{decrease.toLocaleString()}{")"}
      </span>
    );
  };

  return (
    <Widget additionalClassNames="ni-network-summary">
      <WidgetHeader title={title}>
        <WidgetMenu>
          <NetworkIntegrityWidgetInfoFlyout title="Network Summary" description={<NetworkSummaryDescription/>}/>
        </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
        <div className="ni-network-summary__metrics">
          <div className="d-flex flex-column align-items-center mt-3 mb-2">
            <div>
              <h2 className="mb-0">{getMetricValue(NetworkIntegrityAggregateKey.UniqueProviders)}</h2>
            </div>
            {renderDecreaseIndicator(NetworkIntegrityAggregateKey.UniqueProviders, "summary-providers-decrease")}
            <p className="mt-0">Unique Providers</p>
          </div>

          <div className="d-flex flex-column align-items-center mt-2 mb-2">
            <h2 className="mb-0">{getMetricValue(NetworkIntegrityAggregateKey.UniqueLocations)}</h2>
            {renderDecreaseIndicator(NetworkIntegrityAggregateKey.UniqueLocations, "summary-locations-decrease")}
            <p className="mb-0 mt-0">Unique Locations</p>
            <p className="mt-0 mb-0 color-logan">({getMetricValue(NetworkIntegrityAggregateKey.AccessPoints)} Unique Access Points)</p>
            {renderDecreaseIndicator(NetworkIntegrityAggregateKey.AccessPoints, "summary-access-points-decrease")}
          </div>
        </div>
      </WidgetBody>
    </Widget>);
}

export { NetworkSummaryWidget };
